// This eslint rule is disabled to enable easy migration to react-router 6 where the element prop is introduced and the children can be easily replaced with element prop
/* eslint-disable react/no-children-prop */

import { Login, LoginPage } from '../core/auth/Login';
import { Logout } from '../core/auth/Logout';
import { AccessDenied } from '../core/components/AccessDenied';
import { PageNotFound } from '../core/components/PageNotFound';
import { SessionTimedOut } from '../core/components/SessionTimedOut';
import { UiFeature, UiFeatureLite } from '../feature';
import { notificationsFeature } from '../features/notifications/notifications-feature';
import { userAccountFeature } from '../features/user-account/user-account-feature';
import { Loading } from '../core/components/Loading';
import { setAxiosOktaSessionConfig } from '../core/axios';
import { AppLayout } from '../AppLayout';
import {
  featureMountPathBuilder,
  getUserProfile,
  QuestTncAcceptedStatusStorageKey,
  useAppConfigContext,
} from '../core';
import { getSignInMountPath, OktaUserInfo } from '../core/auth';
import { questFeature } from '../features/products/quest';
import { archivesFeature } from '../features/products/archives';
import { sessionCache } from '../core/cache';
import { adminFeature } from '../features/admin/admin-module-feature';
import { DefaultProductRouter } from '../features/products/common/DefaultProductRouter';
import { USAAFeature } from '../features/products/usaa/usaa-feature';
import { Feature } from '@ssar-app/domain';
import { Redirect, Route, Switch } from 'react-router-dom';
import { lazy, ReactNode, Suspense, useCallback } from 'react';
import { Tokens, OktaAuth } from '@okta/okta-auth-js';
import {
  useOktaAuthManager,
  LoginElementBuilderArgs,
  Renderers,
  OnAuthenticated,
  PathContext,
  SecuredElement,
  buildPathBuilder,
  OktaAuthnAppLoginList,
  useCheckOktaSession,
} from '@ssar-app/okta-authn-react';

const TestPage = lazy(() => import('@/Test'));

const commonRoutesConfig: {
  path: string;
  children: ReactNode;
  exact?: boolean;
}[] = [
  {
    path: '/logout',
    children: <Logout />,
  },
  {
    path: '/page-not-found',
    children: <PageNotFound />,
  },
  {
    path: '/access-denied',
    children: <AccessDenied />,
  },
  {
    path: '/session-timed-out',
    children: <SessionTimedOut />,
  },
  {
    path: '/internal-login',
    exact: true,
    children: <Redirect to={`${getSignInMountPath()}/internal`} />,
  },
];

export const buildCommonRoutes = (): ReactNode[] => {
  return commonRoutesConfig.map(({ path, children, exact }) => {
    return <Route exact={exact} path={path} children={children} key={path} />;
  });
};

const LoginElement = (props: LoginElementBuilderArgs) => {
  const oktaAuthManager = useOktaAuthManager();
  const { oktaClientConfiguration, onAuthenticated } = props;

  const onAuthenticationError = useCallback((err: unknown) => {
    console.error(err);
  }, []);

  const oktaAuth = oktaAuthManager.getForOktaClientCode(
    oktaClientConfiguration.code,
  );

  const isInternalLogin = !!oktaClientConfiguration.isInternal;
  const loginType = oktaClientConfiguration.loginType;

  const { status, error, sessionStateAndTokens } = useCheckOktaSession(
    oktaAuth,
    true,
  );

  if (!oktaAuth) {
    return <>OktaAuth not found.</>;
  }

  if (status !== 'completed') {
    return renderers.renderLoadingElement(
      'Checking for existing session, please wait...',
    );
  } else if (error) {
    return renderers.renderErrorElement(error);
  }

  return (
    <>
      {!!sessionStateAndTokens?.exists && <Redirect to='/' />}
      {!sessionStateAndTokens?.exists && (
        <Suspense fallback={<div>Loading ...</div>}>
          <LoginPage isInternalLogin={isInternalLogin} loginType={loginType}>
            <Login
              oktaAuth={oktaAuth}
              isInternalLogin={isInternalLogin}
              loginType={loginType}
              onError={onAuthenticationError}
              onSuccess={onAuthenticated}
            />
          </LoginPage>
        </Suspense>
      )}
    </>
  );
};

const errorElementBuilderFn = (error: unknown) => {
  console.error(error);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <div>{(error as any)?.toString() || error}</div>;
};

const loadingElementBuilderFn = (...messages: (string | ReactNode)[]) => {
  return <Loading>{messages}</Loading>;
};

const loginElementBuilderFn = (props: LoginElementBuilderArgs) => (
  <LoginElement {...props} />
);

const renderers: Renderers = {
  renderNotFoundElement: () => (
    <>
      <PageNotFound />
    </>
  ),
  renderErrorElement: errorElementBuilderFn,
  renderLoadingElement: loadingElementBuilderFn,
  renderLoginElement: loginElementBuilderFn,
};

const userProfileSetter = async () => {
  const userProfile = await getUserProfile();
  // const entitlements = (userProfile.entitlements as Entitlement[]) || [];
  // if (isSuperAdmin(userProfile)) {
  //   commonRoutesConfig.push({
  //     path: '/',
  //     exact: true,
  //     children: <Redirect to={'/quest'} />,
  //   });
  // } else if (entitlements.length > 0) {
  //   commonRoutesConfig.push({
  //     path: '/',
  //     exact: true,
  //     children: (
  //       <Redirect
  //         to={`/${getShortNameForProductToLower(entitlements[0].context)}`}
  //       />
  //     ),
  //   });
  // }
  OktaUserInfo.setOktaUserInfo(userProfile);
};

const onUserAuthenticatedFnBuilder = (forLogin = false) => {
  return async (oktaAuth: OktaAuth, tokens?: Tokens) => {
    setAxiosOktaSessionConfig({
      getAccessToken() {
        const token = oktaAuth?.getAccessToken();
        return token;
      },
    });

    if (forLogin) {
      if (oktaAuth) {
        oktaAuth
          .handleLoginRedirect(tokens)
          .then(userProfileSetter)
          .then(() => {
            sessionCache.set(QuestTncAcceptedStatusStorageKey, '0');
          });
      }
    } else if (tokens) {
      oktaAuth.tokenManager.setTokens(tokens);
      await userProfileSetter();
    }
  };
};

const onUserAuthenticatedForLogin: OnAuthenticated =
  onUserAuthenticatedFnBuilder(true);

const onUserAuthenticatedForFeature: OnAuthenticated =
  onUserAuthenticatedFnBuilder(false);

export const Routes = () => {
  const { oktaClientConfigurations, features } = useAppConfigContext();

  return (
    <Switch>
      {/* 
        This route is required when using signInWithRedirect, but we are not using it anywhere.
        <Route
          path='/authorization-code-b2e/callback'
          children={(props) => {
            return (
              <>
                <LoginCallback />
              </>
            );
          }}
        /> */}
      {buildCommonRoutes()}
      <Route path={getSignInMountPath()}>
        <OktaAuthnAppLoginList
          onAuthenticated={onUserAuthenticatedForLogin}
          renderers={renderers}
          oktaClientConfigurations={oktaClientConfigurations}
        ></OktaAuthnAppLoginList>
      </Route>
      <Route
        path='/test'
        exact
        children={
          <Suspense fallback={<div>Loading Test Module...</div>}>
            <TestPage />
          </Suspense>
        }
      />
      <Route
        path='/'
        exact
        children={
          <SecuredElement
            onAuthenticated={onUserAuthenticatedForFeature}
            renderers={renderers}
          >
            <DefaultProductRouter />
          </SecuredElement>
        }
      />
      {getFeaturesRouteConfigs(
        [
          adminFeature,
          notificationsFeature,
          userAccountFeature,
          questFeature,
          archivesFeature,
          USAAFeature,
        ],
        features,
        featureMountPathBuilder,
      ).map((routeConfig) => {
        const { mountPath, feature, pathBuilder } = routeConfig;
        return (
          <Route
            key={routeConfig.mountPath}
            path={mountPath}
            children={(_props) => {
              return (
                <PathContext
                  value={{
                    basePath: mountPath,
                    buildPath: pathBuilder,
                  }}
                >
                  <SecuredElement
                    onAuthenticated={onUserAuthenticatedForFeature}
                    renderers={renderers}
                  >
                    <AppLayout feature={feature}>
                      {feature.rootComponent}
                    </AppLayout>
                  </SecuredElement>
                </PathContext>
              );
            }}
          ></Route>
        );
      })}
      <Route>
        <PageNotFound />
      </Route>
    </Switch>
  );
};

export type FeatureRouteConfig = {
  feature: UiFeature;
  mountPath: string;
  pathBuilder: ReturnType<typeof buildPathBuilder>;
};

const getFeaturesRouteConfigs = (
  uiFeatures: UiFeatureLite[],
  features: Feature[],
  mountPathBuilder: (feature: Feature) => string,
): FeatureRouteConfig[] => {
  return (uiFeatures || [])
    .filter((a) => a.isDisabled !== true)
    .map((uiFeatureLite) => {
      const feature = features.find((b) => b.code === uiFeatureLite.code);
      return {
        uiFeatureLite,
        feature,
      };
    })
    .filter((a) => !!a.feature)
    .map(({ uiFeatureLite, feature }) => {
      const uiFeature: UiFeature = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...feature!,
        ...uiFeatureLite,
      };
      const mountPath = mountPathBuilder(uiFeature);
      const pathBuilder = buildPathBuilder(mountPath);
      return {
        mountPath,
        pathBuilder,
        feature: uiFeature,
      };
    });
};
